import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, TextInput } from '@mantine/core';
import { IconMapPin, IconSearch, IconShieldCheck } from '@tabler/icons-react';
import { useRidesSecurityService, RIDES_PROVIDERS, type RidesSecurityExpectedQuery, type RidesSecurityRowView } from '@actschurch/ccms-sdk';
import { useRidesLookups, campusById, serviceTimesFor } from '@/lib/lookups';
import { ridesKeys, RIDE_DAY_POLL_MS } from '@/lib/queryKeys';
import { formatDate, formatTime, toYmd } from '@/lib/format';
import { photoRules, useCopy } from '@/lib/settings';
import { PageIntro } from '@/components/rides/PageIntro';
import { ServiceTimePicker } from '@/components/rides/ServiceTimePicker';
import { EmptyState, ErrorState, LoadingState, SectionCard } from '@/components/ui';
import { DriverCard, isChecked } from './DriverCard';
import '@/styles/member.css';

const CAMPUS_KEY = 'rides.security.campusId';
type DateMode = 'today' | 'tomorrow' | 'other';

function readCampus(): number | null {
  try {
    const v = Number(localStorage.getItem(CAMPUS_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}
function saveCampus(id: number | null) {
  try {
    if (id) localStorage.setItem(CAMPUS_KEY, String(id));
    else localStorage.removeItem(CAMPUS_KEY);
  } catch { /* storage blocked: the guard picks the campus again next time */ }
}

function dayOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toYmd(d);
}

const norm = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[\s-]/g, '');

function matchesSearch(row: RidesSecurityRowView, q: string): boolean {
  if (!q) return true;
  const n = norm(q);
  return [row.driverName, row.vehicle?.plate, ...row.riders.flatMap((r) => [r.riderName, r.childName])].some((v) => norm(v).includes(n));
}

/**
 * Campus security: pick the campus (remembered on this device), the day and optionally the
 * service; see who is expected with photo, number plate and check status; tap "Checked at
 * pick-up" or "Checked at drop-off". Drivers still to check come first.
 */
export default function SecurityPage() {
  const lookups = useRidesLookups();
  const { t, settings } = useCopy();
  const photos = photoRules(settings);
  const campuses = lookups.data?.campuses ?? [];
  const [locationId, setLocationId] = useState<number | null>(readCampus);
  const [dateMode, setDateMode] = useState<DateMode>('today');
  const [otherDate, setOtherDate] = useState<string>(dayOffset(2));
  const [serviceTimeId, setServiceTimeId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // A remembered campus starts with the pickers folded into one summary line.
  const [pickersOpen, setPickersOpen] = useState(() => readCampus() === null);

  // Forget a remembered campus that no longer exists.
  useEffect(() => {
    if (lookups.isSuccess && locationId && !campusById(lookups.data, locationId)) setLocationId(null);
  }, [lookups.isSuccess, lookups.data, locationId]);

  const campus = campusById(lookups.data, locationId);
  const date = dateMode === 'today' ? dayOffset(0) : dateMode === 'tomorrow' ? dayOffset(1) : otherDate;
  const query: RidesSecurityExpectedQuery | null = campus && date ? { locationId: campus.locationID, date, serviceTimeId } : null;
  const security = useRidesSecurityService();
  const expected = useQuery({
    queryKey: ridesKeys.security.expected(query ?? { locationId: 0, date: '' }),
    queryFn: () => security.getExpected(query!),
    enabled: !!query,
    refetchInterval: RIDE_DAY_POLL_MS,
  });
  const provider = lookups.data?.settings?.verificationProvider ?? RIDES_PROVIDERS.Manual;

  const rows = useMemo(() => (expected.data?.rows ?? []).filter((r) => matchesSearch(r, search.trim())), [expected.data, search]);
  const allCount = expected.data?.rows.length ?? 0;
  const allDone = (expected.data?.rows ?? []).filter(isChecked).length;
  const allToCheck = allCount - allDone;
  const toCheck = rows.filter((r) => !isChecked(r));
  const done = rows.filter(isChecked);
  const updated = expected.dataUpdatedAt ? new Date(expected.dataUpdatedAt) : null;
  const pad = (n: number) => String(n).padStart(2, '0');

  const chooseCampus = (id: number) => {
    setLocationId(id);
    saveCampus(id);
    setServiceTimeId(null);
    setSearch('');
  };

  const service = campus ? serviceTimesFor(campus).find((st) => st.serviceTimeID === serviceTimeId) : undefined;
  const serviceLabel = service ? `${formatTime(service.startTime)} service` : 'Every service';

  const card = (r: RidesSecurityRowView) => (
    <DriverCard key={`${r.rideAvailabilityID}-${r.direction}`} row={r} provider={provider} photos={photos} onChanged={() => expected.refetch()} />
  );

  return (
    <div className="rides-main">
      <PageIntro title="Campus security" lede={t('security.instructions')} />

      {!(campus && !pickersOpen) && (
      <SectionCard title="Where and when">
        <div className="rm-desk-field">
          <span className="lbl" id="rm-campus-label">Campus</span>
          {lookups.isLoading ? (
            <LoadingState variant="spinner" label="Loading campuses" />
          ) : (
            <div className="rm-chips" role="radiogroup" aria-labelledby="rm-campus-label">
              {campuses.map((c) => (
                <button key={c.locationID} type="button" role="radio" aria-checked={locationId === c.locationID} className="rides-chip" onClick={() => chooseCampus(c.locationID)}>
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="rm-desk-field">
          <span className="lbl" id="rm-day-label">Day</span>
          <div className="rm-chips" role="radiogroup" aria-labelledby="rm-day-label">
            {(['today', 'tomorrow', 'other'] as DateMode[]).map((m) => (
              <button key={m} type="button" role="radio" aria-checked={dateMode === m} className="rides-chip" onClick={() => setDateMode(m)}>
                {m === 'today' ? 'Today' : m === 'tomorrow' ? 'Tomorrow' : 'Another day'}
              </button>
            ))}
          </div>
          {dateMode === 'other' && (
            <TextInput mt="sm" type="date" label="Choose the day" value={otherDate} onChange={(e) => setOtherDate(e.currentTarget.value)} />
          )}
        </div>
        {campus && serviceTimesFor(campus).length > 0 && (
          <div className="rm-desk-field">
            <span className="lbl">Service</span>
            <ServiceTimePicker times={serviceTimesFor(campus)} value={serviceTimeId} onChange={setServiceTimeId} allowAny campusName={campus.name} />
          </div>
        )}
        {campus && (
          <Button fullWidth mt="lg" onClick={() => setPickersOpen(false)}>
            Show the drivers
          </Button>
        )}
      </SectionCard>
      )}

      {!campus ? (
        <div className="rm-section">
          <EmptyState icon={<IconShieldCheck size={28} />} title="Choose your campus">{t('security.choose_campus')}</EmptyState>
        </div>
      ) : (
        <>
          <section className="rm-deskstat rm-rise" aria-label="Today's checks">
            {!pickersOpen && (
              <div className="rm-deskplace" style={{ marginBottom: 14 }}>
                <span className="tile" aria-hidden="true"><IconMapPin size={22} /></span>
                <span className="txt">
                  <b>{campus.name}</b>
                  <span>{dateMode === 'today' ? 'Today' : dateMode === 'tomorrow' ? 'Tomorrow' : formatDate(date)}. {serviceLabel}.</span>
                </span>
                <button type="button" className="rm-editbtn" style={{ margin: 0 }} onClick={() => setPickersOpen(true)} aria-label="Change the campus, day or service">Change</button>
              </div>
            )}
            {expected.data && allCount > 0 ? (
              <>
                <div className="nums">
                  <div className="num todo"><b>{allToCheck}</b><span>{allToCheck === 1 ? 'driver to check' : 'drivers to check'}</span></div>
                  <div className="num done"><b>{allDone}</b><span>checked</span></div>
                </div>
                <div className="bar" role="progressbar" aria-label="Drivers checked" aria-valuemin={0} aria-valuemax={allCount} aria-valuenow={allDone}>
                  <span style={{ width: `${allCount ? Math.round((allDone / allCount) * 100) : 0}%` }} />
                </div>
              </>
            ) : null}
            <p className="meta">
              {formatDate(date)}.{' '}
              {updated ? `Updated ${pad(updated.getHours())}:${pad(updated.getMinutes())}. ` : ''}
              {t('security.refresh_note')}
            </p>
          </section>
          <div style={{ height: 14 }} />
          {expected.isLoading && <LoadingState label="Loading expected drivers" count={2} />}
          {expected.isError && (
            <ErrorState title="We couldn't load the drivers" error={expected.error} onRetry={() => expected.refetch()} retrying={expected.isRefetching} />
          )}
          {expected.data && allCount === 0 && (
            <EmptyState icon={<IconShieldCheck size={28} />} title="No lifts expected">
              {t('security.empty', undefined, { campus: campus.name })}
            </EmptyState>
          )}

          {allCount > 3 && (
            <TextInput
              label="Find a driver"
              placeholder="Name or number plate"
              leftSection={<IconSearch size={18} aria-hidden="true" />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              mb="md"
            />
          )}
          {expected.data && allCount > 0 && rows.length === 0 && <p className="rm-muted">Nobody matches "{search.trim()}".</p>}

          {toCheck.length > 0 && (
            <>
              {done.length > 0 && <h3 className="rm-groupttl">Still to check ({toCheck.length})</h3>}
              <div className="rm-stack">{toCheck.map(card)}</div>
            </>
          )}
          {done.length > 0 && (
            <>
              <h3 className="rm-groupttl">Checked ({done.length})</h3>
              <div className="rm-stack">{done.map(card)}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}
